import { Injectable } from '@angular/core';
import { PictureForm } from './form.service';
import { Subject, Subscription, BehaviorSubject } from 'rxjs';
import { HttpClientService } from '../connections/http-client.service';

@Injectable({
    providedIn: 'root'
})
export class ImageUploadService {
    private pictures: BehaviorSubject<Map<string, PictureForm>>;

    private imageSubject = new Subject<string[]>();
    private imageStack: string[] = [];

    private imagesUploadedSubject = new Subject<number>();
    private queueSubscription: Subscription;

    private carUid: string;

    public uploading: boolean;

    constructor(private http: HttpClientService) {}

    private subToUpload() {
        this.queueSubscription = this.imageSubject.subscribe((array) => {
            if (array.length > 0) {
                this.uploadImage(array[0]);
            } else {
                this.uploading = false;
            }
        });
    }

    initService(map: BehaviorSubject<Map<string, PictureForm>>, carUid: string) {
        this.pictures = map;
        this.carUid = carUid;

        this.subToUpload();
    }

    destroyService() {
        if (this.queueSubscription) {
            this.queueSubscription.unsubscribe();
            this.imageStack.splice(0, this.imageStack.length);
        }
    }

    isQueueEmpty() {
        return this.imageStack.length === 0;
    }

    addToUploadQueue(position: string) {
        if (!this.imageStack.includes(position)) {
            this.imageStack.push(position);
        }
        if (!this.uploading) {
            this.imageSubject.next(this.imageStack);
        }
    }

    private finishUpload(position: string) {
        this.imageStack.splice(this.imageStack.indexOf(position), 1);
        this.imageSubject.next(this.imageStack);
    }

    private tryAgainAfterTimeout() {
        setTimeout(() => {
            this.imageSubject.next(this.imageStack);
        }, 3000);
    }

    private refreshCounter() {
        let counter = 0;
        for (let [key, value] of this.pictures.value) {
            if (value.uploaded) {
                counter++;
            }
        }
        this.imagesUploadedSubject.next(counter);
    }

    private async uploadImage(position: string) {
        const currentPictures = this.pictures.value;

        this.uploading = true;
        currentPictures.set(position, {
            image: currentPictures.get(position).image,
            error: false,
            uploaded: false,
            taken: true
        });
        const pictureParts = currentPictures.get(position).image.split('base64,');

        try {
            await this.http.post(
                'upload_image',
                {
                    item_uid: this.carUid,
                    description: position,
                    image_base64: pictureParts[1]
                },
                (data) => {
                    return data;
                },
                { showDefaultAlert: false }
            );

            currentPictures.set(position, {
                image: currentPictures.get(position).image,
                error: false,
                uploaded: true,
                taken: true
            });
            this.refreshCounter();
            this.finishUpload(position);
        } catch {
            currentPictures.set(position, {
                image: currentPictures.get(position).image,
                error: true,
                uploaded: false,
                taken: true
            });
            this.refreshCounter();
            this.tryAgainAfterTimeout();
        } finally {
            this.pictures.next(currentPictures);
        }
    }
}
